import { useApiQuery } from '../use-api.js';
import { VerdictPill } from '../components/VerdictPill.js';
import type { CoverageResponse } from '../lib/types.js';

export function Coverage() {
  const query = useApiQuery<CoverageResponse>(['coverage'], '/api/test-env-coverage');

  if (query.isLoading) return <p>Loading coverage…</p>;
  if (query.isError || !query.data) return <p className="error">Failed to load coverage.</p>;

  const { repos, counts } = query.data;
  const overallCounts = Object.entries(counts?.overall ?? {});

  return (
    <section>
      <h2>Test-env coverage</h2>

      <p className="section-intro">
        Whether Borg can actually run each repo's tests. <strong>Deps</strong> is the
        in-container readiness of dependencies + the test DB, from the repo's most recent
        run. <strong>Secrets</strong> is whether the agent-required secrets that repo needs
        are configured in SSM. <strong>Overall</strong> combines them — a repo missing
        required secrets is <code>NOT_READY</code> even if its dependencies install cleanly.
      </p>

      <dl className="legend">
        <dt><VerdictPill verdict="READY" /></dt>
        <dd>Dependencies and test DB came up — tests can run.</dd>
        <dt><VerdictPill verdict="DEGRADED" /></dt>
        <dd>A dependency install or the test DB failed to set up.</dd>
        <dt><VerdictPill verdict="CONFIGURED" /></dt>
        <dd>All agent-required secrets are configured in SSM.</dd>
        <dt><VerdictPill verdict="MISSING" /></dt>
        <dd>One or more agent-required secrets are not configured in SSM.</dd>
        <dt><VerdictPill verdict="NOT_READY" /></dt>
        <dd>Overall: a required secret is missing, so tests will fail regardless of deps.</dd>
        <dt><VerdictPill verdict="NOT_APPLICABLE" /></dt>
        <dd>Nothing to set up / no secrets required for this repo.</dd>
        <dt><VerdictPill verdict="UNKNOWN" /></dt>
        <dd>Not captured / not yet audited.</dd>
      </dl>

      <div className="counts">
        {overallCounts.length === 0 && <span className="empty">No counts.</span>}
        {overallCounts.map(([verdict, n]) => (
          <span key={verdict} className="count-chip">
            <VerdictPill verdict={verdict} /> <strong>{n}</strong>
          </span>
        ))}
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Repo</th>
            <th>Deps</th>
            <th>Secrets</th>
            <th>Overall</th>
            <th>Last run</th>
          </tr>
        </thead>
        <tbody>
          {(repos ?? []).length === 0 && (
            <tr><td colSpan={5} className="empty">No repos.</td></tr>
          )}
          {(repos ?? []).map((r) => (
            <tr key={r.repo}>
              <td>{r.repo}</td>
              <td data-col="deps"><VerdictPill verdict={r.deps} /></td>
              <td data-col="secrets">
                <VerdictPill verdict={r.secrets} />
                {r.missing && r.missing.length > 0 && (
                  <span className="missing-secrets" title={r.missing.join(', ')}> {r.missing.join(', ')}</span>
                )}
              </td>
              <td data-col="overall"><VerdictPill verdict={r.overall} /></td>
              <td>{r.created_at ? r.created_at.slice(0, 16) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
